
import React, { useContext } from 'react';
import { Box, Button, Chip, Divider, FormControlLabel, Radio, RadioGroup, Checkbox, Stack, Typography, Alert } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { tokens } from '../../theme';
import { TitleScreen, SubTitleScreen } from '../../SystemDesign/Styles';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import MuiFullScreenDialog from '../../SystemDesign/MuiFullScreenDialog';
import Timer from '../../SystemDesign/Timer';
import ProgressBar from '../../SystemDesign/ProgressBar';
import { useGetQuizzesQuery, useGetQuizQuestionsQuery, useStartQuizAttemptMutation, useSaveAttemptResponseMutation, useGetAttemptsQuery, useGetQuizAssignmentsQuery } from '../../services/SkillsManagement';
import { skipToken } from '@reduxjs/toolkit/query';
import AttepmtDialog from './AttepmtDialog';
import { AuthContext, useAuth } from '../../Context/AuthContext';
import ViewToggle from '../../SystemDesign/ViewToggle';
import PosteQuizView from './PosteQuizView';
import AssignedQuizzesView from './AssignedQuizzesView';
import SuggestedQuizzesView from './SuggestedQuizzesView';

const AddQuizEvaluation = ({ open, onClose, posteId, quizId, totalTimeSec, initialViewMode = 'poste', inline = false, onAttemptUpdated, isSelfAssessment = false }) => {
  const {isEmployee} = useContext(AuthContext);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const pid = Number(posteId);
  const isPosteMissing = !Number.isFinite(pid) || pid <= 0;
  // Resolve quizId: use prop if provided, otherwise find via quizzes list filtered by poste
  const qidFromProp = Number(quizId);
  const { user } = useAuth();
  // When user picks a suggested quiz from other postes, we override with this id
  const [selectedQuizId, setSelectedQuizId] = React.useState(null);
  // When starting from Assigned view, track the quiz's posteId to pass to startAttempt
  const [selectedQuizPosteId, setSelectedQuizPosteId] = React.useState(null);
  // view toggle: poste | assigned | suggested
  const [viewMode, setViewMode] = React.useState(initialViewMode);
  React.useEffect(() => {
    // Sync when prop changes while dialog is open
    setViewMode(initialViewMode);
  }, [initialViewMode]);

  // When the dialog (non-inline) opens, reset state so we start from pre-start screen every time
  React.useEffect(() => {
    if (!inline && open) {
      setStarted(false);
      setCurrent(0);
      setAnswers({});
      setAttemptId(null);
      setTimerKey((k) => k + 1);
      setSelectedQuizId(null);
      setSelectedQuizPosteId(null);
    }
  }, [open, inline]);
  const shouldFindQuiz = !Number.isFinite(qidFromProp) || qidFromProp <= 0;
  const { data: quizzesData } = useGetQuizzesQuery(
    shouldFindQuiz
      ? { page: 1, limit: 1, search: undefined, isActive: true, posteId: pid }
      : skipToken,
    { skip: !pid || !shouldFindQuiz, refetchOnMountOrArgChange: true }
  );
  const resolvedQuizId = React.useMemo(() => {
    // Priority: user-selected suggestion -> prop -> first quiz for poste
    if (Number.isFinite(Number(selectedQuizId)) && Number(selectedQuizId) > 0) return Number(selectedQuizId);
    if (Number.isFinite(qidFromProp) && qidFromProp > 0) return qidFromProp;
    const arr = quizzesData?.data?.quizzes || quizzesData?.quizzes || [];
    const first = Array.isArray(arr) ? arr[0] : undefined;
    // GetQuizzesApiResponse uses `quizId`
    const id = first?.quizId ?? first?.id ?? first?.quiz_id;
    return Number(id);
  }, [qidFromProp, quizzesData, selectedQuizId]);

  // Suggested quizzes from other postes (active)
  const { data: allQuizzesData } = useGetQuizzesQuery(
    { page: 1, limit: 12, isActive: true },
    { skip: inline ? false : !open, refetchOnMountOrArgChange: true }
  );
  // suggested scroller handled in subview
  const suggestedQuizzes = React.useMemo(() => {
    const arr = allQuizzesData?.data?.quizzes || allQuizzesData?.quizzes || [];
    const list = Array.isArray(arr) ? arr : [];
    return list
      .filter((q) => Number(q?.posteId ?? q?.poste_id) !== pid)
      .filter((q) => Number(q?.quizId ?? q?.id) !== Number(resolvedQuizId))
      .slice(0, 6);
  }, [allQuizzesData, pid, resolvedQuizId]);

  // Assigned quizzes view now handles its own data fetching (pending only)

  // Fetch questions by quiz id (service expects quiz id)
  const { data, isLoading } = useGetQuizQuestionsQuery(
    { id: resolvedQuizId },
    { skip: !Number.isFinite(resolvedQuizId) || resolvedQuizId <= 0 }
  );
  const questions = React.useMemo(() => {
    const arr = data?.data?.questions || data?.questions || [];
    return Array.isArray(arr) ? arr.map((q, i) => ({
      id: q?.questionId ?? q?.id,
      questionText: q?.questionText || '',
      questionType: q?.questionType || 'single_choice',
      points: Number(q?.points ?? 0),
      timeLimit: q?.timeLimit != null ? Number(q?.timeLimit) : null,
      order: Number(q?.questionOrder ?? q?.order ?? i),
      options: (Array.isArray(q?.options) ? q.options : []).map((o, idx) => ({
        id: o?.questionOptionId ?? o?.optionId ?? o?.id ?? `${i}-${idx}`,
        optionText: o?.optionText || '',
        isCorrect: Boolean(o?.isCorrect), // not shown to user, but may exist
        order: Number(o?.optionOrder ?? o?.order ?? idx),
      })),
    })) : [];
  }, [data]);

  // Find quiz (metadata) from quizzes list for timeLimit and attempts
  const quizFromList = React.useMemo(() => {
    const arr1 = quizzesData?.data?.quizzes || quizzesData?.quizzes || [];
    const arr2 = allQuizzesData?.data?.quizzes || allQuizzesData?.quizzes || [];
    const arr = [...(Array.isArray(arr1) ? arr1 : []), ...(Array.isArray(arr2) ? arr2 : [])];
    return arr.find((q) => Number(q?.quizId ?? q?.id) === Number(resolvedQuizId));
  }, [quizzesData, allQuizzesData, resolvedQuizId]);

  // Attempts used from backend for this user and quiz (authoritative)
  const { data: attemptsData, refetch: refetchAttempts } = useGetAttemptsQuery(
    Number.isFinite(resolvedQuizId) && resolvedQuizId > 0 && Number.isFinite(Number(user?.id))
      ? { quizId: Number(resolvedQuizId), userId: Number(user?.id), page: 1, limit: 1 }
      : skipToken,
    { skip: !Number.isFinite(resolvedQuizId) || resolvedQuizId <= 0 || !Number.isFinite(Number(user?.id)) }
  );
  const attemptsUsedApi = React.useMemo(() => {
    return Number(attemptsData?.data?.pagination?.total ?? attemptsData?.pagination?.total ?? 0);
  }, [attemptsData]);

  // Last attempt snapshot and in-progress detector
  const lastAttempt = React.useMemo(() => {
    const arr = attemptsData?.data?.quizAttempts || attemptsData?.quizAttempts || [];
    return Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
  }, [attemptsData]);
  const hasInProgress = React.useMemo(() => {
    const s = String(lastAttempt?.status || '').toLowerCase();
    return s === 'in_progress' || s === 'in progress';
  }, [lastAttempt]);

  // Derive total seconds for timer:
  // 1) explicit prop totalTimeSec
  // 2) quiz timeLimit (minutes) -> seconds
  // 3) sum of per-question time limits (already in seconds)
  // 4) default 15 min
  const derivedTotalSec = React.useMemo(() => {
    if (Number.isFinite(Number(totalTimeSec)) && Number(totalTimeSec) > 0) return Number(totalTimeSec);
    const limitMin = Number(quizFromList?.timeLimit);
    if (Number.isFinite(limitMin) && limitMin > 0) return limitMin * 60;
    const sum = questions.reduce((acc, q) => acc + (Number.isFinite(q.timeLimit) ? Number(q.timeLimit) : 0), 0);
    return sum > 0 ? sum : 15 * 60;
  }, [totalTimeSec, quizFromList, questions]);

  // Attempt lifecycle hooks
  const [startAttempt, { isLoading: starting }] = useStartQuizAttemptMutation();
  const [saveResponse] = useSaveAttemptResponseMutation();

  const [attemptId, setAttemptId] = React.useState(null);
  const [started, setStarted] = React.useState(false);
  const [current, setCurrent] = React.useState(0);
  const [timerKey, setTimerKey] = React.useState(0);
  // answers map: questionId -> Set of option ids
  const [answers, setAnswers] = React.useState({});
  const [attemptDialogOpen, setAttemptDialogOpen] = React.useState(false);

  const currentQuestion = questions[current];
  const total = questions.length;
  const progress = total > 0 ? Math.round(((current + 1) / total) * 100) : 0;
  const computedMaxScore = React.useMemo(() => questions.reduce((sum, q) => sum + (Number(q.points) || 0), 0), [questions]);

  // Quiz metadata for pre-start screen
  const quizMeta = React.useMemo(() => {
    const found = quizFromList;
    return {
      title: found?.title || 'Quiz',
      description: found?.description || '',
      timeLimitMin: Number(found?.timeLimit) || Math.ceil(derivedTotalSec / 60),
      maxAttempts: Number(found?.maxAttempts ?? 1),
      posteName: found?.poste?.name || found?.posteName || '',
      questionsCount: Number(found?.questionsCount ?? questions.length),
    };
  }, [quizFromList, derivedTotalSec, questions.length]);

  const [currentAttempt, setCurrentAttempt] = React.useState(1);
  const [attemptCount, setAttemptCount] = React.useState(0); // local tracker for attempts started in this session

  const handleStart = async () => {
    if (started) return;
    // Ensure we have a valid quiz id before starting
    if (!Number.isFinite(resolvedQuizId) || resolvedQuizId <= 0) return;
    // If there is an in-progress last attempt, resume it instead of starting a new one
    if (hasInProgress && Number.isFinite(Number(lastAttempt?.attemptId))) {
      setAttemptId(Number(lastAttempt.attemptId));
      setStarted(true);
      return;
    }
    // Enforce max attempts based on backend count (only when not resuming)
    const maxAllowed = Number(quizMeta.maxAttempts || 1);
    if (Number.isFinite(maxAllowed) && attemptsUsedApi >= maxAllowed) {
      // Prevent starting a new attempt
      window?.alert?.('You have reached the maximum number of attempts for this quiz.');
      return;
    }
    try {
      // Backend shape may vary; we pass common identifiers. PosteId is optional if not available.
      const effectivePosteId = Number.isFinite(Number(selectedQuizPosteId)) && Number(selectedQuizPosteId) > 0 ? Number(selectedQuizPosteId) : (Number.isFinite(pid) && pid > 0 ? pid : undefined);
      const startBody = { quizId: resolvedQuizId };
      if (Number.isFinite(effectivePosteId) && effectivePosteId > 0) startBody.posteId = effectivePosteId;
      const res = await startAttempt({ body: startBody }).unwrap();
      const id = res?.data?.attemptId ?? res?.attemptId ?? res?.id;
      if (id) setAttemptId(id);
      let apiAttempt = res?.data?.attemptNumber ?? res?.attemptNumber ?? res?.currentAttempt;
      // Decide displayed attempt: prefer API if it's a valid number and > local, else increment local
      setAttemptCount((prev) => {
        const nextLocal = prev + 1;
        const next = Number.isFinite(Number(apiAttempt)) && Number(apiAttempt) > prev ? Number(apiAttempt) : nextLocal;
        setCurrentAttempt(next);
        return next;
      });
      setStarted(true);
      // Inform parent so tables can refresh tentative/status columns
      try { onAttemptUpdated?.(); } catch {}
      // refresh attempts count from backend after starting
      setTimeout(() => {
        try { refetchAttempts?.(); } catch {}
      }, 100);
    } catch (e) {
      // Fallback: still allow local execution so UI is usable
      setStarted(true);
      // If start failed but user insisted, at least bump attempt locally
      setAttemptCount((prev) => {
        const next = prev + 1;
        setCurrentAttempt(next);
        return next;
      });
    }
  };

  const setSingleAnswer = async (questionId, optionId) => {
    const oid = Number(optionId);
    setAnswers((prev) => ({ ...prev, [questionId]: new Set([oid]) }));
    // do not send to backend immediately; we'll batch-save on Submit
  };

  const toggleMultiAnswer = async (questionId, optionId, checked) => {
    setAnswers((prev) => {
      const set = new Set(prev[questionId] || []);
      checked ? set.add(optionId) : set.delete(optionId);
      return { ...prev, [questionId]: set };
    });
  };

  const handleNext = () => setCurrent((i) => Math.min(i + 1, total - 1));
  const handlePrev = () => setCurrent((i) => Math.max(i - 1, 0));

  const handleSubmit = async () => {
    // Send all responses in one go, then open attempt dialog
    if (!attemptId) {
      // if attempt hasn't started, start it
      await handleStart();
    }
    try {
      const saves = Object.entries(answers)
        .filter(([, set]) => set && (set.size || 0) > 0)
        .map(([qid, set]) => {
          const q = questions.find((qq) => Number(qq.id) === Number(qid));
          const optionIds = Array.from(set).map((x) => Number(x));
          if (q?.questionType === 'multiple_choice') {
            return saveResponse({ attemptId, body: { questionId: Number(qid), optionIds } });
          }
          const oid = optionIds[0];
          return saveResponse({ attemptId, body: { questionId: Number(qid), optionId: oid, optionIds: [oid] } });
        });
      if (saves.length > 0) await Promise.allSettled(saves);
    } catch (e) {
      // ignore errors here; submit/evaluate will still proceed
    }
    // Open attempt dialog to handle submit + evaluation
    setAttemptDialogOpen(true);
  };

  const onTimeUp = () => handleSubmit();

  const header = (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <QuizOutlinedIcon color="primary" fontSize="large" />
          <Typography variant="h6">Quiz Evaluation</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Timer key={timerKey} totalSeconds={derivedTotalSec} running={started} onComplete={onTimeUp} />
          <Chip size="small" variant="outlined" label={`Attempt ${(attemptsUsedApi || attemptCount || currentAttempt)}/${quizMeta.maxAttempts}`} />
          <Chip size="small" color="warning" label={`Question ${Math.min(current + 1, total)}/${total || 0}`} />
        </Stack>
      </Stack>
      <ProgressBar value={progress} height="6px" />
    </Stack>
  );

  // Pre-start screen content
  const preStart = (
    <Box sx={{ minHeight: 420, bgcolor: 'transparent', p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header title + subtitle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Typography sx={TitleScreen(colors)}>Quiz Evaluation</Typography>
          <Typography sx={SubTitleScreen(colors)}>
            Choose between Position, Assigned, or Suggested quizzes.
          </Typography>
        </div>
      </div>

      {/* Controls row with ViewToggle under subtitle, aligned left */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '1rem' }}>
        <ViewToggle
          value={viewMode}
          onChange={(v) => setViewMode(v)}
          label="View:"
          items={[
            { value: 'poste', label: <Typography variant="body2">Position Quiz</Typography> },
            { value: 'assigned', label: <Typography variant="body2">Assigned Quiz</Typography> },
            { value: 'suggested', label: <Typography variant="body2">Suggested Quiz</Typography> },
          ]}
        />
        <div style={{ flexGrow: 1 }} />
      </div>

      {isPosteMissing && (
        <Alert severity="warning">
          {(Boolean(isSelfAssessment) || Boolean(isEmployee))
            ? 'You do not have a position assigned. Please assign a position to yourself or ask an administrator to assign one before continuing.'
            : 'The selected employee does not have an assigned position. Please define a position for this employee first.'}
        </Alert>
      )}
      {/* Contextual helper text for the selected view (colored note) */}
      <Alert
        severity="info"
        variant="outlined"
        sx={{
          mb: 2,
          mt: -1,
          bgcolor: alpha(colors.primary[100], 0.1),
          borderColor: colors.primary[200],
        }}
      >
        {viewMode === 'poste' && (
          <span>Position Quiz: quizzes that evaluate skills required for your current position (job).</span>
        )}
        {viewMode === 'assigned' && (
          <span>Assigned Quiz: quizzes explicitly assigned to you by a manager or administrator.</span>
        )}
        {viewMode === 'suggested' && (
          <span>Suggested Quiz: recommended quizzes from other positions to help you expand or validate related skills.</span>
        )}
      </Alert>

      {/* Poste Quiz card */}
      {viewMode === 'poste' && (
        <PosteQuizView
          quizMeta={quizMeta}
          total={total}
          hasInProgress={hasInProgress}
          attemptsUsedApi={attemptsUsedApi}
          starting={starting}
          resolvedQuizId={resolvedQuizId}
          onStart={handleStart}
        />
      )}

      {/* Assigned quizzes view */}
      {viewMode === 'assigned' && (
        <AssignedQuizzesView
          open={inline ? true : open}
          onStart={(payload) => {
            const qid = typeof payload === 'object' ? payload?.qid : payload;
            const pId = typeof payload === 'object' ? payload?.posteId : undefined;
            setSelectedQuizId(Number(qid));
            if (Number.isFinite(Number(pId))) setSelectedQuizPosteId(Number(pId));
            setStarted(false);
            setCurrent(0);
            setAnswers({});
            setAttemptId(null);
            setTimerKey((k) => k + 1);
            setTimeout(() => { try { handleStart(); } catch {} }, 150);
          }}
        />
      )}

      {/* Suggested quizzes from other postes view */}
      {viewMode === 'suggested' && (
        <SuggestedQuizzesView
          open={inline ? true : open}
          posteId={pid}
          excludeQuizId={resolvedQuizId}
          onSelect={(sid) => {
            setSelectedQuizId(sid);
            setStarted(false);
            setCurrent(0);
            setAnswers({});
            setAttemptId(null);
            setTimerKey((k) => k + 1);
            setTimeout(() => { try { handleStart(); } catch {} }, 200);
          }}
        />
      )}
    </Box>
  );

  const questionsBody = (
    <Stack spacing={2}>
      {header}
      <Divider />

      {/* Question area */}
      {!Number.isFinite(resolvedQuizId) || resolvedQuizId <= 0 ? (
        <Typography variant="body2" color="text.secondary">No quiz is linked to this position. Please select or configure a quiz for this evaluation.</Typography>
      ) : isLoading ? (
        <Typography variant="body2">Loading questions...</Typography>
      ) : total === 0 ? (
        <Typography variant="body2" color="text.secondary">No questions found for this quiz.</Typography>
      ) : (
        <Box>
          {/* Question type helper message */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {currentQuestion?.questionType === 'multiple_choice'
              ? 'Answer this question with multiple choice'
              : currentQuestion?.questionType === 'single_choice'
              ? 'Answer this question with single choice'
              : 'Answer this question'}
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6">{currentQuestion?.questionText}</Typography>
            {!!currentQuestion?.points && <Chip size="small" variant="outlined" label={`${currentQuestion.points} pts`} />}
          </Stack>

          {/* Answers */}
          {currentQuestion?.questionType === 'multiple_choice' ? (
            <Stack>
              {currentQuestion.options.map((o) => (
                <FormControlLabel
                  key={o.id}
                  control={
                    <Checkbox
                      checked={Boolean(answers[currentQuestion.id]?.has?.(o.id))}
                      onChange={(e) => toggleMultiAnswer(currentQuestion.id, o.id, e.target.checked)}
                    />
                  }
                  label={o.optionText}
                />
              ))}
            </Stack>
          ) : (
            <RadioGroup
              value={String(Array.from(answers[currentQuestion.id] || [])[0] || '')}
              onChange={(e) => setSingleAnswer(currentQuestion.id, Number(e.target.value))}
            >
              {currentQuestion.options.map((o) => (
                <FormControlLabel key={o.id} value={String(o.id)} control={<Radio />} label={o.optionText} />
              ))}
            </RadioGroup>
          )}

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="space-between">
            <Button variant="outlined" disabled={current === 0} onClick={handlePrev}>Previous</Button>
            {current < total - 1 ? (
              <Button variant="contained" onClick={handleNext}>Next</Button>
            ) : (
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!attemptId}>
                Submit
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* Start overlay */}
      {!started && total > 0 && (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Button size="large" variant="contained" onClick={handleStart} disabled={starting || (!hasInProgress && (Number(quizMeta.maxAttempts||1) <= attemptsUsedApi))}>Start Quiz</Button>
        </Box>
      )}
    </Stack>
  );

  const body = started ? questionsBody : preStart;

  if (inline) {
    return (
      <>
        {body}
        <AttepmtDialog
          key="attempt-dialog"
          open={attemptDialogOpen}
          onClose={() => setAttemptDialogOpen(false)}
          attemptId={attemptId}
          quizId={resolvedQuizId}
          maxAttempts={quizMeta.maxAttempts}
          currentAttempt={currentAttempt}
          attemptsUsed={attemptsUsedApi || attemptCount || currentAttempt}
          computedMaxScore={computedMaxScore}
          onStartAgain={async () => {
            setAttemptDialogOpen(false);
            setStarted(false);
            setCurrent(0);
            setAnswers({});
            setAttemptId(null);
            setTimerKey((k) => k + 1);
            await new Promise((r) => setTimeout(r, 50));
            handleStart();
            try { onAttemptUpdated?.(); } catch {}
          }}
          onFinish={() => {
            setAttemptDialogOpen(false);
            try { onAttemptUpdated?.(); } catch {}
          }}
        />
      </>
    );
  }

  return (
    <>
      <MuiFullScreenDialog
        open={open}
        onClose={onClose}
        title=""
        subtitle=""
        content={body}
        maxWidth="md"
      />
      <AttepmtDialog
        key="attempt-dialog"
        open={attemptDialogOpen}
        onClose={() => setAttemptDialogOpen(false)}
        attemptId={attemptId}
        quizId={resolvedQuizId}
        maxAttempts={quizMeta.maxAttempts}
        currentAttempt={currentAttempt}
        attemptsUsed={attemptsUsedApi || attemptCount || currentAttempt}
        computedMaxScore={computedMaxScore}
        onStartAgain={async () => {
          // Reset state and restart a new attempt if allowed
          setAttemptDialogOpen(false);
          setStarted(false);
          setCurrent(0);
          setAnswers({});
          setAttemptId(null);
          setTimerKey((k) => k + 1);
          // Start a new attempt
          await new Promise((r) => setTimeout(r, 50));
          handleStart();
          try { onAttemptUpdated?.(); } catch {}
        }}
        onFinish={() => {
          setAttemptDialogOpen(false);
          try { onAttemptUpdated?.(); } catch {}
          onClose?.();
        }}
      />
    </>
  );
};

export default AddQuizEvaluation;

